import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CalendarX2,
  Check,
  CreditCard,
  Download,
  PiggyBank,
  Sparkles,
  Timer,
  X,
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { MarketingLayout } from '@/components/marketing/MarketingLayout';
import { CtaLink, Eyebrow, PageHero, Reveal, SectionHeading } from '@/components/marketing/primitives';
import { cn } from '@/lib/utils';
import { PLANS, PRICING_FAQS } from '@/lib/marketingSeo';
import '@/styles/marketing-pages.css';

interface Tier {
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  tagline: string;
  cta: string;
  highlight?: boolean;
  features: { label: string; included: boolean }[];
}

// Plan names, prices and features are shared with the structured data and
// llms.txt (src/lib/marketingSeo.ts), so they can't drift apart.
const tiers: Tier[] = PLANS.map((plan) => ({ ...plan, cta: 'Start free trial', highlight: plan.name === 'Team' }));

const everyPlan = [
  { icon: Timer, label: 'Live in 60 seconds' },
  { icon: Sparkles, label: '30 days free' },
  { icon: PiggyBank, label: 'Zero commission' },
  { icon: CreditCard, label: 'Take card, PayPal or cash' },
  { icon: Download, label: 'Export your data any time' },
  { icon: CalendarX2, label: 'Cancel any time' },
];

const pricingFaqs = PRICING_FAQS;

const MarketingPricing = () => {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const isAnnual = billing === 'annual';

  return (
    <MarketingLayout>
      <PageHero
        eyebrow={
          <>
            <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--mk-sun))]" /> 30 days free on every plan
          </>
        }
        title={
          <>
            Pays for itself <span className="mk-mark mk-mark-block text-[hsl(var(--mk-ink))]">in one clean.</span>
          </>
        }
        lede="One flat monthly fee. No setup costs, no commission, no contract. Try it free for 30 days and only stay if it’s winning you work."
      >
        <div className="mk-toggle" role="group" aria-label="Billing period">
          {(['monthly', 'annual'] as const).map((option) => (
            <button key={option} type="button" aria-pressed={billing === option} onClick={() => setBilling(option)}>
              {option === 'monthly' ? 'Monthly' : 'Annual'}
              {option === 'annual' && (
                <span className="rounded-full bg-[hsl(var(--mk-mint))] px-2 py-0.5 text-[11px] text-[hsl(var(--mk-ink))]">
                  Save
                </span>
              )}
            </button>
          ))}
        </div>
        <p className="mt-4 text-sm text-white/55">
          Connect a domain you already own on Team and Business.
        </p>
      </PageHero>

      {/* Tier cards, lifted over the hero */}
      <section className="relative z-10 -mt-12 md:-mt-14">
        <div className="container mx-auto grid max-w-6xl px-4 gap-5 lg:grid-cols-3 lg:items-stretch">
          {tiers.map((t, i) => {
            const price = isAnnual ? t.annualPrice : t.monthlyPrice;
            const saving = t.monthlyPrice * 12 - t.annualPrice;
            const dark = t.highlight;
            return (
              <Reveal
                key={t.name}
                as="article"
                delay={i * 90}
                className={cn(
                  'relative flex flex-col rounded-[2rem] p-8 md:p-9',
                  dark
                    ? 'mk-ink-bg mk-on-dark shadow-[0_40px_80px_-36px_rgba(20,16,70,0.75)]'
                    : 'mk-card'
                )}
              >
                {dark && (
                  <span className="absolute -top-3.5 left-8 rounded-full bg-[hsl(var(--mk-sun))] px-3 py-1 text-xs font-semibold text-[hsl(var(--mk-ink))]">
                    Most popular
                  </span>
                )}
                <h2 className={cn('font-display text-2xl', dark && 'text-white')}>{t.name}</h2>
                <p className={cn('mt-2 min-h-[3rem]', dark ? 'text-white/65' : 'text-[hsl(var(--mk-muted))]')}>{t.tagline}</p>

                <div className="mt-7 flex items-baseline gap-1.5">
                  <span className={cn('font-display text-6xl font-semibold tracking-[-0.04em]', dark && 'text-white')}>£{price}</span>
                  <span className={dark ? 'text-white/60' : 'text-[hsl(var(--mk-muted))]'}>/ {isAnnual ? 'year' : 'month'}</span>
                </div>
                <p
                  className={cn(
                    'mt-2 h-5 text-sm font-semibold',
                    dark ? 'text-[hsl(var(--mk-mint))]' : 'text-[hsl(var(--mk-mint-deep))]'
                  )}
                >
                  {isAnnual ? `Save £${saving} a year` : 'Billed monthly, cancel any time'}
                </p>

                <Link
                  to="/signup"
                  className={cn('mk-btn mt-7 w-full', dark ? 'mk-btn-sun' : 'mk-btn-ink')}
                >
                  {t.cta} <ArrowRight className="h-4 w-4" />
                </Link>

                <ul className={cn('mt-8 space-y-3.5 border-t pt-7', dark ? 'border-white/15' : 'border-[hsl(var(--border))]')}>
                  {t.features.map((f) => (
                    <li
                      key={f.label}
                      className={cn(
                        'flex items-start gap-3',
                        !f.included && (dark ? 'text-white/35' : 'text-[hsl(var(--mk-ink)/0.35)]'),
                        f.included && dark && 'text-white'
                      )}
                    >
                      <span
                        className={cn(
                          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                          f.included
                            ? 'bg-[hsl(var(--mk-mint))] text-[hsl(var(--mk-ink))]'
                            : dark
                              ? 'bg-white/10'
                              : 'bg-[hsl(var(--mk-panel))]'
                        )}
                      >
                        {f.included ? <Check className="h-3 w-3" strokeWidth={3} /> : <X className="h-3 w-3" />}
                      </span>
                      <span className={cn(!f.included && 'line-through decoration-current/40')}>{f.label}</span>
                      {/* The AI tools are new: flag them where the plan includes them. */}
                      {f.included && f.label.startsWith('AI ') && (
                        <span className="mt-0.5 shrink-0 rounded-full bg-[hsl(var(--mk-sun))] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--mk-ink))]">
                          New
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </Reveal>
            );
          })}
        </div>
        <p className="mt-8 text-center text-sm text-[hsl(var(--mk-muted))]">
          No setup fee. Change plan or cancel any time.
        </p>
      </section>

      {/* Every plan includes */}
      <section className="px-4 pt-24 md:pt-28">
        <Reveal className="mk-panel container mx-auto px-6 py-12 md:px-12">
          <div className="grid gap-8 lg:grid-cols-[0.75fr_1.25fr] lg:items-center">
            <h2 className="font-display text-3xl leading-tight md:text-4xl">Every plan, no exceptions.</h2>
            <ul className="grid gap-x-8 gap-y-5 sm:grid-cols-2 md:grid-cols-3">
              {everyPlan.map((item) => (
                <li key={item.label} className="flex items-center gap-3 font-medium">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[hsl(var(--mk-violet))]">
                    <item.icon className="h-5 w-5" />
                  </span>
                  {item.label}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* Why not a website builder */}
      <section className="py-28 md:py-36">
        <div className="container mx-auto grid gap-14 px-4 lg:grid-cols-2 lg:items-center lg:gap-20">
          <Reveal>
            <Eyebrow>Why not just use a website builder?</Eyebrow>
            <h2 className="mk-h2 mt-6">
              Squarespace won’t send a cleaner. Calendly won’t <span className="mk-mark mk-mark-mint">price an end of tenancy.</span>
            </h2>
            <p className="mk-lede mt-6 text-[hsl(var(--mk-muted))]">
              A website builder gives you a nice-looking site that can’t run your diary. A booking app gives you time slots that know nothing about prices, deposits or which cleaner is free. The Polished Version is built for one thing, running a cleaning company, and does all of it for one flat fee.
            </p>
          </Reveal>
          <div className="grid gap-4">
            {[
              { k: 'Website', v: 'Templates made for cleaners, edited visually' },
              { k: 'Bookings', v: 'Online booking and quote requests, 24/7' },
              { k: 'Schedule', v: 'One weekly view, cleaners assigned in a click' },
              { k: 'Team', v: 'Logins that show each cleaner their own jobs' },
            ].map((row, i) => (
              <Reveal key={row.k} delay={i * 80} className="mk-card flex items-center gap-5 p-5">
                <span className="flex-1 sm:flex sm:items-center sm:gap-5">
                  <span className="block font-display text-lg font-semibold sm:w-28 sm:shrink-0">{row.k}</span>
                  <span className="mt-0.5 block text-[hsl(var(--mk-muted))] sm:mt-0 sm:flex-1">{row.v}</span>
                </span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--mk-mint))]">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
              </Reveal>
            ))}
            <Reveal delay={360} className="mk-ink-bg flex items-center justify-between gap-4 rounded-[1.75rem] p-5 pl-6">
              <span className="font-display text-lg font-semibold text-white">All of it, from</span>
              <span className="font-display text-4xl font-semibold tracking-tight text-[hsl(var(--mk-sun))]">
                £9<span className="text-base font-medium text-white/60">/month</span>
              </span>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Pricing questions */}
      <section className="pb-28 md:pb-36">
        <div className="container mx-auto max-w-3xl px-4">
          <SectionHeading eyebrow="Good questions" title="The money bits, answered." />
          <Reveal className="mt-12">
            <Accordion type="single" collapsible defaultValue="q-0">
              {pricingFaqs.map((item, i) => (
                <AccordionItem key={item.q} value={`q-${i}`} className="mk-faq-item">
                  <AccordionTrigger className="mk-faq-trigger">{item.q}</AccordionTrigger>
                  <AccordionContent className="pb-6 pr-10 text-base leading-relaxed text-[hsl(var(--mk-muted))]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal className="mt-10 flex justify-center">
            <CtaLink to="/faq" variant="ghost">
              Read every FAQ
            </CtaLink>
          </Reveal>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default MarketingPricing;
